import React, { useContext, useMemo } from 'react'
import config from 'constants/config'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import AdditionalGuestsPopup from 'components/Common/AdditionalGuestsPopup/AdditionalGuestsPopup'
import { arePricesLoading, isBNBLAvailable, getCheapestAvailablePackageOption, getFromDurationLabel } from 'selectors/offerPage/offerPageSelectors'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import TextButton from 'LuxKit/Button/TextButton'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import Caption from 'LuxKit/Typography/Caption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import WalledContent from 'components/Common/WalledContent'
import { generateLoyaltyPointsCalculatorHotelOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import Group from 'components/utils/Group'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import { getActivePackagesByRoom, getMultiRoomPackageTotals, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { getHotelOfferDisplayedValue } from 'lib/offer/getOfferDisplayPricing'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import FlashHotelWePriceMatch from '../FlashHotelWePriceMatch'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import GeoContext from 'contexts/geoContext'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'

function getDiscountPercentage(
  showDiscountPill: boolean,
  isMultiRoom: boolean,
  multipleRoomHighestDiscountPercent: number | undefined,
  isBundledWithFlightsOnly: boolean,
  bundledDiscountPercent: number,
  hotelDiscountPercent: number,
): number | undefined {
  if (!showDiscountPill) return undefined

  if (isMultiRoom) {
    return multipleRoomHighestDiscountPercent
  }
  return isBundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent
}

function calculateShouldShowValue({
  isMultiRoom,
  hotelValue,
  hotelPrice,
  multiRoomShouldDisplayValue,
  multiRoomHotelValue,
  multiRoomHotelPrice,
  pkgShouldDisplayValue,
}: {
  isMultiRoom: boolean
  hotelValue: number
  hotelPrice: number
  multiRoomShouldDisplayValue: boolean
  multiRoomHotelValue: number
  multiRoomHotelPrice: number
  pkgShouldDisplayValue: boolean
}): boolean {
  if (isMultiRoom) {
    return multiRoomHotelValue > multiRoomHotelPrice && multiRoomShouldDisplayValue
  }
  return hotelValue > hotelPrice && pkgShouldDisplayValue
}

function calculateOriginalValue({
  isMultiRoom,
  bundledWithFlightsOnly,
  hotelValue,
  flightPrice,
  multiRoomHotelValue,
  multiRoomFlightPrice,
  isFlightSelected,
}: {
  isMultiRoom: boolean
  bundledWithFlightsOnly: boolean
  hotelValue: number
  flightPrice: number
  multiRoomHotelValue: number
  multiRoomFlightPrice: number
  isFlightSelected: boolean
}): number {
  if (isMultiRoom) {
    if (bundledWithFlightsOnly) {
      return multiRoomHotelValue + multiRoomFlightPrice
    } else {
      return isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue
    }
  } else {
    if (bundledWithFlightsOnly) {
      return hotelValue + flightPrice
    } else {
      return hotelValue
    }
  }
}

function shouldDisplayPill(
  isMultiRoom: boolean,
  isOfferDiscountPillHidden: boolean,
  isBundledWithFlightsOnly: boolean,
  hotelDiscountPercent: number,
  bundledDiscountPercent: number,
  multipleRoomHighestDiscountPercent: number,
) {
  if (isMultiRoom) {
    const shouldShowDiscountPercent = multipleRoomHighestDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    return shouldShowDiscountPercent && !isOfferDiscountPillHidden
  } else {
    const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
    return !isOfferDiscountPillHidden && (shouldShowDiscountPercent && !isBundledWithFlightsOnly) || (isBundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  }
}

const defaultMultiRoomTotals: MultiRoomPackageTotals = {
  flightPrice: 0,
  hotelMemberPrice: 0,
  hotelPlusFlightsMemberPrice: 0,
  hotelPlusFlightsPrice: 0,
  hotelPrice: 0,
  hotelValue: 0,
}

interface Props {
  offer: App.Offer
  rooms: Array<OfferPageRoom>
  cheapestOption: App.OfferPackageOptionView
  durationLabel: string
  flightOrigin?: App.Airport
  airports: Array<App.Airport>
  showHotelPriceWithFlights: boolean
  isFlightSelected: boolean
  isMultiRoom: boolean
  multiRoomPackageTotals: MultiRoomPackageTotals
  multiRoomPackages: PackageOptionViewByRoom
  charterBundleRoute?: App.CharterBundleRoute
  priceMode: OfferPagePriceMode
}

function FlashOfferIntroPricingBlock(props: Props) {
  const {
    offer,
    cheapestOption: packageView,
    rooms,
    durationLabel,
    flightOrigin,
    airports,
    showHotelPriceWithFlights,
    isFlightSelected,
    isMultiRoom,
    multiRoomPackageTotals,
    multiRoomPackages,
    charterBundleRoute,
    priceMode,
  } = props
  const {
    flightPrice,
    hotelValue,
    hotelPrice,
    hotelMemberPrice,
    package: pkg,
    hotelDiscountPercent,
    bundledDiscountPercent,
    propertyFees,
    taxesAndFees,
  } = packageView

  const {
    hotelPrice: multiRoomHotelPrice,
    hotelMemberPrice: multiRoomHotelMemberPrice,
    hotelPlusFlightsPrice: multiRoomHotelFlightPrice,
    hotelPlusFlightsMemberPrice: multiRoomMemberFlightPrice,
    flightPrice: multiRoomFlightPrice,
    hotelValue: multiRoomHotelValue,
    shouldDisplayValue: multiRoomShouldDisplayValue,
  } = multiRoomPackageTotals

  const {
    highestDiscountPercent: multipleRoomHighestDiscountPercent,
  } = getHotelOfferDisplayedValue(!!offer.bundledWithFlightsOnly, isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice, isFlightSelected ? multiRoomHotelValue + multiRoomFlightPrice : multiRoomHotelValue, !!multiRoomShouldDisplayValue)

  const totalPrice = useMemo(() => {
    let total = hotelPrice + (propertyFees ?? 0)
    if (offer.bundledWithFlightsOnly) {
      total += flightPrice
    }
    return total
  }, [flightPrice, hotelPrice, offer.bundledWithFlightsOnly, propertyFees])

  const totalMemberPrice = useMemo(() => {
    let total = hotelMemberPrice
    if (hotelMemberPrice > 0) {
      total = hotelMemberPrice + (propertyFees ?? 0)
      if (offer.bundledWithFlightsOnly) {
        total += flightPrice
      }
    }
    return total
  }, [flightPrice, hotelMemberPrice, offer.bundledWithFlightsOnly, propertyFees])

  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])
  const showDiscountPill = shouldDisplayPill(isMultiRoom, !!offer?.isDiscountPillHidden, !!offer.bundledWithFlightsOnly, hotelDiscountPercent, bundledDiscountPercent, multipleRoomHighestDiscountPercent)

  const showPriceDetails = taxesAndFees > 0 || (propertyFees ?? 0) > 0 || config.agentHub.isEnabled
  const shouldShowValue = calculateShouldShowValue({
    isMultiRoom,
    hotelValue,
    hotelPrice,
    multiRoomShouldDisplayValue: !!multiRoomShouldDisplayValue,
    multiRoomHotelValue,
    multiRoomHotelPrice,
    pkgShouldDisplayValue: pkg.shouldDisplayValue,
  })

  // Get charter flight price when charterBundleRoute is present (similar to FlashOfferSummary)
  const { totalFlightPrice: charterFlightPrice } = useOfferPageFlightPrice()
  const charterRoute = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined
  const bundledWithFlightsOnly = offer.bundledWithFlightsOnly || !!charterRoute
  const hasCharterFlight = !!charterRoute && !!charterFlightPrice && !offer.flightCacheDisabled

  // Use charter flight price for value calculation when charter route is present
  const effectiveFlightPriceForValue = charterRoute && hasCharterFlight ? charterFlightPrice : flightPrice
  const effectiveMultiRoomFlightPriceForValue = charterRoute && hasCharterFlight ? charterFlightPrice : multiRoomFlightPrice

  const originalValue = useMemo(() => {
    return calculateOriginalValue({
      isMultiRoom,
      bundledWithFlightsOnly: !!offer.bundledWithFlightsOnly || !!charterRoute,
      hotelValue,
      flightPrice: effectiveFlightPriceForValue,
      multiRoomHotelValue,
      multiRoomFlightPrice: effectiveMultiRoomFlightPriceForValue,
      isFlightSelected,
    })
  }, [isMultiRoom, offer.bundledWithFlightsOnly, charterRoute, hotelValue, effectiveFlightPriceForValue, multiRoomHotelValue, effectiveMultiRoomFlightPriceForValue, isFlightSelected])

  const saleUnit = useMemo(() => {
    const unit = offer.bundledWithFlightsOnly ? 'package*' : 'total'
    if (priceMode === 'perPerson') {
      return unit.includes('*') ? 'person*' : 'person'
    }
    return unit
  }, [offer.bundledWithFlightsOnly, priceMode])

  const { currentRegionCode } = useContext(GeoContext)

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration }),
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      packageView,
      skip: !offer.bundledWithFlightsOnly,
    }),
  ], [offer, packageView, currentRegionCode])

  const multiRoomLoyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration })
    ))
  }, [offer, multiRoomPackages, currentRegionCode])

  const multiRoomPointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...multiRoomLoyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice: multiRoomFlightPrice,
      skip: !isFlightSelected,
    }),
  ], [offer, isFlightSelected, multiRoomFlightPrice, multiRoomLoyaltyHotelOptions])

  const flightDestination = useMemo(() => airports.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])
  const [deals] = useFlightDeals('all', {
    originAirportCode: flightOrigin?.code,
    destinationAirportCode: flightDestination?.code,
  })
  const showFlightsDealTag = flightOrigin && flightDestination && deals.length > 0 && !charterBundleRoute

  const offerPrice = useMemo(() => {
    if (isMultiRoom) {
      const multiRoomPrice = isFlightSelected ? multiRoomHotelFlightPrice : multiRoomHotelPrice
      // For charter bundle, add flight price to hotel price even in multi-room mode
      if (charterRoute && hasCharterFlight) {
        return multiRoomPrice + charterFlightPrice
      }
      return multiRoomPrice
    }
    // For charter bundle, add flight price to hotel price
    if (charterRoute && hasCharterFlight) {
      return totalPrice + charterFlightPrice
    }
    return totalPrice
  }, [isMultiRoom, totalPrice, isFlightSelected, multiRoomHotelFlightPrice, multiRoomHotelPrice, charterRoute, hasCharterFlight, charterFlightPrice])

  const offerMemberPrice = useMemo(() => {
    if (isMultiRoom) {
      const multiRoomMemberPrice = isFlightSelected ? multiRoomMemberFlightPrice : multiRoomHotelMemberPrice
      // For charter bundle, add flight price to hotel price even in multi-room mode
      if (charterRoute && hasCharterFlight) {
        return multiRoomMemberPrice > 0 ? multiRoomMemberPrice + charterFlightPrice : 0
      }
      return multiRoomMemberPrice
    }
    // For charter bundle, add flight price to hotel price
    if (charterRoute && hasCharterFlight) {
      return totalMemberPrice > 0 ? totalMemberPrice + charterFlightPrice : 0
    }
    return totalMemberPrice
  }, [isMultiRoom, multiRoomHotelMemberPrice, isFlightSelected, totalMemberPrice, multiRoomMemberFlightPrice, charterRoute, hasCharterFlight, charterFlightPrice])

  const convertToDisplay = useMemo(() => getPriceDisplayFunction(priceMode, rooms), [priceMode, rooms])

  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag) && !charterBundleRoute

  if (showHotelPriceWithFlights && !isMultiRoom) {
    return <WalledContent enforced={offer.walledGarden}>
      <Group direction="vertical" gap={12} horizontalAlign="center">
        <Group direction="vertical" fullWidth gap={12}>
          {showDiscountPill && <PriceRowValueDiscount type="estimate" discountPercentage={hotelDiscountPercent} />}
          <AccommodationPackageTilePricingBlocks>
            <VerticalSpacer gap={12}>
              <BodyTextBlock variant="large" weight="bold">Hotel</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={convertToDisplay(totalMemberPrice)}
                    price={convertToDisplay(totalPrice)}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    testid="hotel-summary-price"
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={convertToDisplay(hotelValue)}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={packageView.flightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">View {config.brandConfig.travelSynonym} options</TextButton>}
                    />}
                  />
                </div>
              </VerticalSpacer>
            </VerticalSpacer>
            <VerticalSpacer gap={12}>
              <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">Hotel + flights</BodyTextBlock>
              <VerticalSpacer gap={8}>
                <div>
                  <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>
                  <LuxPlusPriceStack
                    memberPrice={convertToDisplay(totalMemberPrice > 0 ? totalMemberPrice + flightPrice : 0)}
                    price={convertToDisplay(totalPrice + flightPrice)}
                    size="L"
                    saleUnit={saleUnit}
                    offerConfig={offer.luxPlus}
                    testid="hotel-summary-price"
                  />
                  {showIncludedGuestsExceeded && (
                    <AdditionalGuestsPopup
                      complex={false}
                      description="+ Extra guest surcharge"
                      modalContent={pkg.roomPolicyDescription}
                    />
                  )}
                  {shouldShowValue && <PriceRowValueDiscountWithCaption
                    data-testid="value-box"
                    size="L"
                    originalValue={convertToDisplay(hotelValue + flightPrice)}
                  />}
                  <PriceRowTaxesAndFees
                    endIcon={showPriceDetails && <OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      duration={pkg.duration}
                      propertyFees={packageView.propertyFees}
                      taxesAndFees={packageView.taxesAndFees}
                      hotelPrice={packageView.hotelPrice}
                      hotelMemberPrice={packageView.hotelMemberPrice}
                      showMemberPrice={packageView.hotelMemberPrice > 0}
                      flightPrice={packageView.flightPrice}
                      rooms={rooms}
                      extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
                      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">View {config.brandConfig.travelSynonym} options</TextButton>}
                    />}
                  />
                </div>
                {showFlightsDealTag && <UrgencyLabel variant="critical1" icon={<SolidFireIcon />}>Flight deal available</UrgencyLabel>}
              </VerticalSpacer>
            </VerticalSpacer>
          </AccommodationPackageTilePricingBlocks>
        </Group>
        <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" />
        {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
      </Group>
    </WalledContent>
  }
  return <WalledContent enforced={offer.walledGarden}>
    <VerticalSpacer gap={8}>
      <div>
        {!isMultiRoom && <PriceRowPriceCaption>{durationLabel}</PriceRowPriceCaption>}
        {isMultiRoom &&
          <PriceRowPriceCaption>
            <MobileMultipleRoomsDurationLabel shouldDisplayRoom />
          </PriceRowPriceCaption>}
        <LuxPlusPriceStack
          memberPrice={convertToDisplay(offerMemberPrice)}
          price={convertToDisplay(offerPrice)}
          size="L"
          saleUnit={saleUnit}
          offerConfig={offer.luxPlus}
          testid="hotel-summary-price"
        />
        {offer.bundledWithFlightsOnly && !rooms && <Caption variant="medium" colour="neutral-three">
        &nbsp;Two Adults
        </Caption>}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            description="+ Extra guest surcharge"
            modalContent={pkg.roomPolicyDescription}
          />
        )}
        {shouldShowValue && <PriceRowValueDiscountWithCaption
          data-testid="value-box"
          size="L"
          originalValue={convertToDisplay(originalValue)}
          discountPercentage={getDiscountPercentage(
            !!showDiscountPill,
            isMultiRoom,
            multipleRoomHighestDiscountPercent,
            !!offer.bundledWithFlightsOnly,
            bundledDiscountPercent,
            hotelDiscountPercent,
          )}
        />}
        {!isMultiRoom && <PriceRowTaxesAndFees
          endIcon={showPriceDetails && <OfferPriceDetailsRow
            offerPage
            offer={offer}
            duration={pkg.duration}
            propertyFees={packageView.propertyFees}
            taxesAndFees={packageView.taxesAndFees}
            hotelPrice={packageView.hotelPrice}
            hotelMemberPrice={packageView.hotelMemberPrice}
            showMemberPrice={packageView.hotelMemberPrice > 0}
            flightPrice={bundledWithFlightsOnly ? (charterRoute && hasCharterFlight ? charterFlightPrice : packageView.flightPrice) : undefined}
            rooms={rooms}
            extraGuestMessage={showIncludedGuestsExceeded ? pkg.roomPolicyDescription : ''}
            dueAtPropertyMessage={offer.property?.taxesAndFeesContent ? offer.property.taxesAndFeesContent : ''}
            trigger="info-icon"
            cta={
              <TextButton
                kind="primary"
                fit="flex"
              >
                View {config.brandConfig.travelSynonym} options
              </TextButton>
            }
          />}
        />}
      </div>
      {!isMultiRoom && <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" /> }
      {isMultiRoom && <LoyaltyPointsEarn calculationRequests={multiRoomPointsEarnCalculationRequests} calculationType="estimate" /> }
      {isWePriceMatchEnabled && <FlashHotelWePriceMatch offer={offer} />}
    </VerticalSpacer>
  </WalledContent>
}

export default withOfferPageAndReduxState((state, appState) => {
  const isMultiRoom = isMultipleRoomBookingActive(state, appState)
  return {
    cheapestOption: getCheapestAvailablePackageOption(state, appState),
    rooms: state.rooms,
    isFlightSelected: state.flights,
    offer: state.offer,
    flightOrigin: state.airport,
    calendarsLoading: arePricesLoading(appState, state),
    isBNBLAvailable: isBNBLAvailable(state),
    isSpoofed: appState.auth.account.isSpoofed,
    durationLabel: getFromDurationLabel(state, appState),
    airports: appState.geo.airports,
    multiRoomPackages: getActivePackagesByRoom(state, appState),
    isMultiRoom,
    multiRoomPackageTotals: isMultiRoom ? getMultiRoomPackageTotals(state, appState) : defaultMultiRoomTotals,
    charterBundleRoute: state.charterBundleRoute,
    priceMode: state.priceMode,
  }
}, FlashOfferIntroPricingBlock)
